// PendulumOrbitRise.jsx
// Resonant orbit rise via EMMS pendulum — parametric energy transfer
// Analogy: pumping a swing by shifting weight at the right phase each orbit
import { useState, useEffect, useRef, useCallback } from "react";

const MONO  = "'JetBrains Mono','Courier New',monospace";
const BG    = "#020810";
const GOLD  = "#ffd060";
const CYAN  = "#00d4ff";
const GREEN = "#00ff9d";
const PUR   = "#c084fc";
const RED   = "#ff4d6d";
const MUTED = "#2a4a6a";

// ── Physics constants ─────────────────────────────────────────────────────────
const MU       = 3.986e14;         // Earth µ  (m³/s²)
const R_E      = 6.371e6;          // Earth radius (m)
const R0       = R_E + 500e3;      // initial orbit radius (500 km)
const M_HULL   = 8;                // kg
const M_EMMS   = 1;                // kg
const M_TOTAL  = M_HULL + M_EMMS;
const L_STROKE = 0.032;            // m  (half-stroke amplitude)
const F_ACT    = 0.8;              // N  (EMMS actuate force)
const SIM_SPD  = 8000;             // × real time (so one orbit ≈ 0.7 s wall)

// ── Derived ───────────────────────────────────────────────────────────────────
function orbitalPeriod(r) { return 2*Math.PI*Math.sqrt(r**3/MU); }
function orbitalVelocity(r) { return Math.sqrt(MU/r); }

// ── Canvas helpers ────────────────────────────────────────────────────────────
function circle(ctx, x, y, r, fill, stroke, lw=1) {
  ctx.beginPath(); ctx.arc(x,y,r,0,Math.PI*2);
  if (fill)  { ctx.fillStyle=fill; ctx.fill(); }
  if (stroke){ ctx.strokeStyle=stroke; ctx.lineWidth=lw; ctx.stroke(); }
}
function rr(ctx,x,y,w,h,r) {
  ctx.beginPath();
  ctx.moveTo(x+r,y); ctx.lineTo(x+w-r,y); ctx.arcTo(x+w,y,x+w,y+r,r);
  ctx.lineTo(x+w,y+h-r); ctx.arcTo(x+w,y+h,x+w-r,y+h,r);
  ctx.lineTo(x+r,y+h); ctx.arcTo(x,y+h,x,y+h-r,r);
  ctx.lineTo(x,y+r); ctx.arcTo(x,y,x+r,y,r);
  ctx.closePath();
}

// ── Fresh simulation state ────────────────────────────────────────────────────
function freshSim(freqRatio) {
  const T0 = orbitalPeriod(R0);
  return {
    t: 0,
    sat_theta: 0,          // satellite angle in orbit (rad)
    orbit_r: R0,           // current semi-major axis (m)
    orbit_a: R0,           // semi-major axis
    orbit_e: 0,            // eccentricity
    emms_x: 0,             // EMMS mass relative position (m)
    emms_v: 0,             // EMMS relative velocity (m/s)
    delta_v_total: 0,      // accumulated Δv (m/s)
    orbit_count: 0,
    last_theta: 0,
    r_history: [],         // [{t, r}] sampled history for chart
    phase_history: [],     // [{orb, emms}] for phase portrait (last 200)
    energy_history: [],    // [{t, E}] orbital energy
    emms_omega: (2*Math.PI/T0)*freqRatio,  // EMMS angular frequency
    freqRatio,
    impulse_log: [],       // [{t, dv, aligned}]
    total_rise_m: 0,
  };
}

// ── Main component ────────────────────────────────────────────────────────────
export default function PendulumOrbitRise() {
  const [playing, setPlaying]     = useState(false);
  const [freqRatio, setFreqRatio] = useState(1.0);  // EMMS freq / orbit freq
  const [showPhase, setShowPhase] = useState(true);
  const [disp, setDisp]           = useState(null);

  const simRef   = useRef(freshSim(1.0));
  const rafRef   = useRef(null);
  const lastTsR  = useRef(null);
  const frameR   = useRef(0);
  const playR    = useRef(false);
  playR.current  = playing;
  const fRatioR  = useRef(freqRatio);
  fRatioR.current = freqRatio;

  const canvOrbit  = useRef(null);
  const canvPend   = useRef(null);
  const canvChart  = useRef(null);

  // ── Physics step ──────────────────────────────────────────────────────────
  function step(s, dt) {
    const T_orb = orbitalPeriod(s.orbit_a);
    const v_orb = orbitalVelocity(s.orbit_a);
    const omega_orb = 2*Math.PI / T_orb;

    // Update EMMS oscillation (SHM driven by actuator at emms_omega)
    // Treat as sinusoidal: x = L_STROKE * sin(emms_omega * t)
    // v = L_STROKE * emms_omega * cos(emms_omega * t)
    const emms_omega = s.emms_omega;
    s.emms_x = L_STROKE * Math.sin(emms_omega * s.t);
    s.emms_v = L_STROKE * emms_omega * Math.cos(emms_omega * s.t);

    // Satellite moves in orbit
    s.sat_theta += omega_orb * dt;
    if (s.sat_theta >= 2*Math.PI) {
      s.sat_theta -= 2*Math.PI;
      s.orbit_count++;
    }

    // Impulse transfer: EMMS stroke imparts Δv to hull
    // At the end of each EMMS half-stroke (when emms_v reverses sign),
    // the mass hits the wall and transfers momentum to the hull.
    // Δv_hull = (M_EMMS / M_TOTAL) * 2 * |emms_v_peak|
    // Direction: same as satellite velocity at moment of transfer.
    // Resonance condition: emms_omega = n * omega_orb → each impulse tangential → orbit rises.
    // Off resonance: impulse directions average to zero → no net rise.

    const emms_phase = emms_omega * s.t;
    const prev_phase = emms_omega * (s.t - dt);
    const crossed_pi = Math.floor(emms_phase/Math.PI) !== Math.floor(prev_phase/Math.PI);

    if (crossed_pi) {
      // Wall impact — compute impulse
      const v_emms_peak = L_STROKE * emms_omega;
      const dv_mag = (M_EMMS / M_TOTAL) * 2 * v_emms_peak;

      // Direction of impulse relative to orbital velocity direction
      // orbital velocity direction = tangential = (−sin θ, cos θ)
      // impulse direction = tangential when in resonance
      // Phase alignment factor = cos(orbital_phase - emms_phase_offset)
      const orb_phase_norm = (s.sat_theta % (2*Math.PI));
      const emms_period_frac = (emms_omega * s.t) % (2*Math.PI);
      // Alignment: dot product of current EMMS push direction with velocity direction
      // When ratio = 1, emms completes one cycle per orbit → always aligned at crossing
      const alignment = Math.cos(orb_phase_norm - emms_period_frac * (1/fRatioR.current));
      const dv_tangential = dv_mag * alignment;

      // Apply Δv to orbital energy → change semi-major axis
      // vis-viva: v² = µ(2/r - 1/a) → dE = v·dv → da = 2a²·v·dv/µ
      const da = 2 * s.orbit_a * s.orbit_a * v_orb * dv_tangential / MU;
      s.orbit_a = Math.max(R_E + 100e3, s.orbit_a + da);
      s.delta_v_total += Math.abs(dv_tangential);
      s.total_rise_m = s.orbit_a - R0;

      // Update emms_omega to track new orbital frequency if ratio locked
      const T_new = orbitalPeriod(s.orbit_a);
      s.emms_omega = (2*Math.PI/T_new) * fRatioR.current;

      s.impulse_log.push({ t: s.t, dv: dv_tangential, aligned: Math.abs(alignment)>0.8 });
      if (s.impulse_log.length > 100) s.impulse_log.shift();
    }

    // Sample history
    if (s.r_history.length === 0 || s.t - s.r_history[s.r_history.length-1].t > T_orb/4) {
      s.r_history.push({ t: s.t, a: s.orbit_a });
      if (s.r_history.length > 300) s.r_history.shift();
    }

    // Phase portrait (sat theta vs emms phase, normalised 0-1)
    const ep = ((emms_omega * s.t) % (2*Math.PI)) / (2*Math.PI);
    const op = (s.sat_theta) / (2*Math.PI);
    s.phase_history.push({ op, ep });
    if (s.phase_history.length > 400) s.phase_history.shift();

    s.t += dt;
  }

  // ── Draw orbit panel ───────────────────────────────────────────────────────
  function drawOrbit(canvas, s) {
    const ctx = canvas.getContext("2d");
    const W=canvas.width, H=canvas.height;
    const CX=W/2, CY=H/2;
    ctx.fillStyle=BG; ctx.fillRect(0,0,W,H);

    // Star field
    if (!drawOrbit._stars) {
      drawOrbit._stars = Array.from({length:60},()=>({
        x:Math.random()*700, y:Math.random()*400,
        r:Math.random()*1.2+0.3, a:Math.random()*0.6+0.2
      }));
    }
    drawOrbit._stars.forEach(st=>{
      ctx.fillStyle=`rgba(200,220,255,${st.a})`;
      ctx.beginPath(); ctx.arc(st.x,st.y,st.r,0,Math.PI*2); ctx.fill();
    });

    // Scale: fit R0 orbit nicely
    const SCALE = (Math.min(W,H)*0.35) / (s.orbit_a * 1.15);

    // Earth
    const er = R_E*SCALE;
    const eg = ctx.createRadialGradient(CX,CY,0,CX,CY,er);
    eg.addColorStop(0,"#1a4a8a"); eg.addColorStop(0.7,"#0d2a5a"); eg.addColorStop(1,"#061525");
    circle(ctx,CX,CY,er,eg,CYAN+"44",1);
    // Atmosphere glow
    const ag = ctx.createRadialGradient(CX,CY,er*0.95,CX,CY,er*1.08);
    ag.addColorStop(0,CYAN+"40"); ag.addColorStop(1,CYAN+"00");
    circle(ctx,CX,CY,er*1.06,ag,null);

    // Initial orbit circle (faint reference)
    const r0px = R0*SCALE;
    ctx.beginPath(); ctx.arc(CX,CY,r0px,0,Math.PI*2);
    ctx.strokeStyle=MUTED+"88"; ctx.lineWidth=0.8; ctx.setLineDash([4,6]);
    ctx.stroke(); ctx.setLineDash([]);
    ctx.fillStyle=MUTED+"66"; ctx.font=`8px ${MONO}`; ctx.textAlign="center";
    ctx.fillText("INITIAL ORBIT 500km",CX, CY-r0px-5);

    // Current orbit (rising) — draw as ellipse with slight eccentricity
    const aCur = s.orbit_a*SCALE;
    const rise = s.orbit_a - R0;
    // Show last N orbit paths for trail effect
    if (s.r_history.length > 1) {
      for (let i=1; i<s.r_history.length; i++) {
        const ri = s.r_history[i].a * SCALE;
        const alpha = (i/s.r_history.length)*0.3;
        ctx.beginPath(); ctx.arc(CX,CY,ri,0,Math.PI*2);
        ctx.strokeStyle=`rgba(0,255,157,${alpha})`; ctx.lineWidth=0.5;
        ctx.setLineDash([]); ctx.stroke();
      }
    }
    // Current orbit bold
    ctx.beginPath(); ctx.arc(CX,CY,aCur,0,Math.PI*2);
    const orbitGrad = ctx.createLinearGradient(CX-aCur,CY,CX+aCur,CY);
    orbitGrad.addColorStop(0,GREEN+"44"); orbitGrad.addColorStop(0.5,GREEN); orbitGrad.addColorStop(1,GREEN+"44");
    ctx.strokeStyle=GREEN; ctx.lineWidth=1.5; ctx.setLineDash([]);
    ctx.stroke();

    // Satellite position
    const sx = CX + Math.cos(s.sat_theta)*aCur;
    const sy = CY - Math.sin(s.sat_theta)*aCur;

    // Satellite glow trail (last 40 positions)
    for (let i=0; i<40; i++) {
      const trail_theta = s.sat_theta - i*0.04;
      const tx = CX + Math.cos(trail_theta)*aCur;
      const ty = CY - Math.sin(trail_theta)*aCur;
      const al = (1-i/40)*0.4;
      circle(ctx,tx,ty,1.5,`rgba(0,212,255,${al})`,null);
    }

    // Satellite body
    ctx.save();
    ctx.translate(sx,sy);
    ctx.rotate(-s.sat_theta+Math.PI/2);
    rr(ctx,-10,-5,20,10,2);
    ctx.fillStyle="#0a2040"; ctx.fill();
    ctx.strokeStyle=CYAN; ctx.lineWidth=1.5; ctx.stroke();
    // Solar panels
    ctx.fillStyle="#1e3a5f";
    ctx.fillRect(-20,-3,8,6); ctx.fillRect(12,-3,8,6);
    // EMMS mass indicator inside satellite
    const emms_px = s.emms_x * 8000 * 0.1; // scaled to pixels
    ctx.fillStyle=GOLD+"cc";
    ctx.beginPath(); ctx.rect(emms_px-2,-2,4,4); ctx.fill();
    ctx.restore();

    // Velocity vector
    const vel_dir_x = -Math.sin(s.sat_theta);
    const vel_dir_y = -Math.cos(s.sat_theta);
    const vLen = 25;
    ctx.strokeStyle=PUR; ctx.lineWidth=1.5;
    ctx.beginPath(); ctx.moveTo(sx,sy); ctx.lineTo(sx+vel_dir_x*vLen, sy+vel_dir_y*vLen);
    ctx.stroke();

    // Labels
    ctx.fillStyle=GREEN; ctx.font=`bold 10px ${MONO}`; ctx.textAlign="left";
    ctx.fillText("ORBIT RISE", 8, 16);
    ctx.fillStyle=GOLD; ctx.font=`11px ${MONO}`;
    ctx.fillText(`+${(rise/1000).toFixed(3)} km altitude gain`, 8, 30);
    ctx.fillStyle=CYAN; ctx.font=`9px ${MONO}`;
    ctx.fillText(`orbit #${s.orbit_count}  ·  a = ${(s.orbit_a/1000-R_E/1000).toFixed(1)} km alt`, 8, 44);

    // Current orbit altitude annotation
    const altKm = (s.orbit_a-R_E)/1000;
    ctx.fillStyle=GREEN+"cc"; ctx.font=`8px ${MONO}`; ctx.textAlign="center";
    ctx.fillText(`${altKm.toFixed(1)} km`, CX, CY - aCur - 8);

    // Resonance badge
    const isRes = Math.abs(fRatioR.current - Math.round(fRatioR.current)) < 0.05 ||
                  Math.abs(fRatioR.current*2 - Math.round(fRatioR.current*2)) < 0.05;
    ctx.fillStyle=isRes?"#031a0a":"#1a0308";
    rr(ctx,W-150,6,144,22,4); ctx.fill();
    ctx.strokeStyle=isRes?GREEN:RED; ctx.lineWidth=1; rr(ctx,W-150,6,144,22,4); ctx.stroke();
    ctx.fillStyle=isRes?GREEN:RED; ctx.font=`bold 8px ${MONO}`; ctx.textAlign="center";
    ctx.fillText(isRes?"✓ RESONANCE LOCKED":"✗ OFF RESONANCE", W-78, 21);
  }

  // ── Draw pendulum panel ────────────────────────────────────────────────────
  function drawPendulum(canvas, s) {
    const ctx = canvas.getContext("2d");
    const W=canvas.width, H=canvas.height;
    ctx.fillStyle=BG; ctx.fillRect(0,0,W,H);

    // Title
    ctx.fillStyle=GOLD; ctx.font=`bold 10px ${MONO}`; ctx.textAlign="center";
    ctx.fillText("EMMS PENDULUM ANALOGY", W/2, 14);

    // ── Left: satellite cross-section showing pendulum ──────────────────────
    const satCX = W/4, satCY = H*0.45;
    const satW=80, satH=50;

    // Satellite hull
    ctx.save();
    ctx.translate(satCX, satCY);
    // Tilt by orbital angle
    const tilt = s.sat_theta * 0.1;
    ctx.rotate(tilt);

    // Hull rect
    rr(ctx,-satW/2,-satH/2,satW,satH,5);
    ctx.fillStyle="#051525"; ctx.fill();
    ctx.strokeStyle=CYAN; ctx.lineWidth=1.5; ctx.stroke();
    ctx.fillStyle=CYAN+"20"; ctx.fill();

    // EMMS track inside
    const trkHalf = L_STROKE*1500;
    ctx.strokeStyle=GOLD+"40"; ctx.lineWidth=1;
    ctx.beginPath(); ctx.moveTo(-trkHalf,0); ctx.lineTo(trkHalf,0); ctx.stroke();
    // End stops
    [-trkHalf, trkHalf].forEach(ox=>{
      ctx.strokeStyle=RED+"88"; ctx.lineWidth=2;
      ctx.beginPath(); ctx.moveTo(ox,-8); ctx.lineTo(ox,8); ctx.stroke();
    });

    // EMMS mass block (sliding on track)
    const emms_px = s.emms_x * 1500;
    const massCol = Math.abs(s.emms_x) > L_STROKE*0.85 ? RED : GOLD;
    ctx.fillStyle=massCol;
    ctx.strokeStyle=massCol;
    ctx.lineWidth=1;
    ctx.beginPath(); ctx.rect(emms_px-7,-9,14,18); ctx.fill(); ctx.stroke();
    // mass label
    ctx.fillStyle=BG; ctx.font=`bold 7px ${MONO}`; ctx.textAlign="center";
    ctx.fillText("1kg",emms_px,4);

    // Pendulum string (connect to pivot at top center)
    const pivotY = -satH/2 - 8;
    ctx.strokeStyle=GOLD+"66"; ctx.lineWidth=1; ctx.setLineDash([2,3]);
    ctx.beginPath(); ctx.moveTo(0,pivotY); ctx.lineTo(emms_px,0); ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle=GOLD+"88"; ctx.beginPath();
    ctx.arc(0,pivotY,3,0,Math.PI*2); ctx.fill();

    ctx.restore();

    // Arrow showing momentum transfer
    const mom = s.emms_v * M_EMMS;
    const momPx = mom * 20;
    if (Math.abs(momPx)>2) {
      const ary = satCY + satH/2 + 14;
      ctx.strokeStyle=PUR; ctx.lineWidth=1.5;
      ctx.fillStyle=PUR;
      const arx = satCX;
      ctx.beginPath(); ctx.moveTo(arx, ary); ctx.lineTo(arx+momPx, ary); ctx.stroke();
      ctx.beginPath();
      const dir = momPx>0?1:-1;
      ctx.moveTo(arx+momPx,ary);
      ctx.lineTo(arx+momPx-dir*7,ary-3);
      ctx.lineTo(arx+momPx-dir*7,ary+3);
      ctx.closePath(); ctx.fill();
      ctx.fillStyle=PUR; ctx.font=`7px ${MONO}`; ctx.textAlign="center";
      ctx.fillText("hull reaction",arx+momPx/2,ary-6);
    }

    // Orbital position indicator (arc)
    const arcX=satCX, arcY=satCY+65, arcR=28;
    ctx.strokeStyle=MUTED; ctx.lineWidth=1;
    ctx.beginPath(); ctx.arc(arcX,arcY,arcR,0,Math.PI*2); ctx.stroke();
    ctx.fillStyle=CYAN; ctx.font=`7px ${MONO}`; ctx.textAlign="center";
    ctx.fillText("ORBIT",arcX,arcY+arcR+10);
    // Sat position on mini orbit
    const msx = arcX + Math.cos(s.sat_theta)*arcR;
    const msy = arcY - Math.sin(s.sat_theta)*arcR;
    circle(ctx,msx,msy,4,CYAN,null);

    // ── Right: phase / waveform diagram ──────────────────────────────────────
    const wx = W/2+10, wy=20, ww=W/2-20, wh=H-40;

    // Background
    ctx.fillStyle="#050f1a"; rr(ctx,wx,wy,ww,wh,4); ctx.fill();
    ctx.strokeStyle=MUTED; ctx.lineWidth=1; rr(ctx,wx,wy,ww,wh,4); ctx.stroke();

    ctx.fillStyle=MUTED; ctx.font=`7px ${MONO}`; ctx.textAlign="center";
    ctx.fillText("PHASE PORTRAIT", wx+ww/2, wy+12);

    // Axis labels
    ctx.fillStyle=MUTED; ctx.font=`7px ${MONO}`; ctx.textAlign="center";
    ctx.fillText("orbital angle →", wx+ww/2, wy+wh-5);
    ctx.save(); ctx.translate(wx+10, wy+wh/2); ctx.rotate(-Math.PI/2);
    ctx.fillText("EMMS phase →",0,0); ctx.restore();

    // Draw phase history as Lissajous pattern
    if (s.phase_history.length > 2) {
      ctx.beginPath();
      const mapX = p => wx+16 + p.op*(ww-24);
      const mapY = p => wy+18 + p.ep*(wh-30);
      ctx.moveTo(mapX(s.phase_history[0]), mapY(s.phase_history[0]));
      for (let i=1; i<s.phase_history.length; i++) {
        const alpha = i/s.phase_history.length;
        ctx.strokeStyle = `rgba(255,208,96,${alpha*0.8})`;
        ctx.lineWidth=1;
        ctx.beginPath();
        if (i>0) {
          ctx.moveTo(mapX(s.phase_history[i-1]),mapY(s.phase_history[i-1]));
          ctx.lineTo(mapX(s.phase_history[i]),mapY(s.phase_history[i]));
          ctx.stroke();
        }
      }
      // Current point
      const last = s.phase_history[s.phase_history.length-1];
      circle(ctx,mapX(last),mapY(last),4,GOLD,null);
    }

    // Resonance label at bottom of phase portrait
    const isInt = Math.abs(fRatioR.current*2 - Math.round(fRatioR.current*2)) < 0.08;
    ctx.fillStyle=isInt?GREEN:RED; ctx.font=`bold 8px ${MONO}`; ctx.textAlign="center";
    ctx.fillText(isInt?"RESONANT — Lissajous closed → orbit rises":"DRIFTING — orbit stays flat", wx+ww/2, wy+wh+14);
  }

  // ── Draw chart ─────────────────────────────────────────────────────────────
  function drawChart(canvas, s) {
    const ctx = canvas.getContext("2d");
    const W=canvas.width, H=canvas.height;
    ctx.fillStyle=BG; ctx.fillRect(0,0,W,H);

    // Panel
    ctx.fillStyle="#050f1a"; rr(ctx,4,4,W-8,H-8,6); ctx.fill();
    ctx.strokeStyle=MUTED; ctx.lineWidth=1; rr(ctx,4,4,W-8,H-8,6); ctx.stroke();

    const PL=50, PR=16, PT=22, PB=28;
    const CW=W-PL-PR, CH2=H-PT-PB;

    ctx.fillStyle=GOLD; ctx.font=`bold 9px ${MONO}`; ctx.textAlign="left";
    ctx.fillText("ALTITUDE GAIN vs TIME", PL, 15);

    if (s.r_history.length < 2) {
      ctx.fillStyle=MUTED; ctx.font=`9px ${MONO}`; ctx.textAlign="center";
      ctx.fillText("Run simulation to see orbit rise...", PL+CW/2, PT+CH2/2);
      return;
    }

    const t0 = s.r_history[0].t;
    const tEnd = s.r_history[s.r_history.length-1].t;
    const maxRise = Math.max(1, Math.max(...s.r_history.map(d=>d.a-R0)));

    // Grid
    ctx.strokeStyle=MUTED+"44"; ctx.lineWidth=0.5;
    for (let i=0; i<=4; i++) {
      const y = PT + CH2 * (1 - i/4);
      ctx.beginPath(); ctx.moveTo(PL,y); ctx.lineTo(PL+CW,y); ctx.stroke();
      ctx.fillStyle=MUTED; ctx.font=`7px ${MONO}`; ctx.textAlign="right";
      ctx.fillText(`+${(maxRise*i/4/1000).toFixed(2)}km`, PL-3, y+3);
    }
    for (let i=0; i<=4; i++) {
      const x = PL + CW*i/4;
      ctx.beginPath(); ctx.moveTo(x,PT); ctx.lineTo(x,PT+CH2); ctx.stroke();
    }

    // Axes
    ctx.strokeStyle=MUTED; ctx.lineWidth=1;
    ctx.beginPath(); ctx.moveTo(PL,PT); ctx.lineTo(PL,PT+CH2); ctx.lineTo(PL+CW,PT+CH2); ctx.stroke();

    // Orbit rise curve
    ctx.beginPath();
    s.r_history.forEach((d,i) => {
      const x = PL + ((d.t-t0)/(tEnd-t0||1)) * CW;
      const y = PT + CH2*(1 - (d.a-R0)/maxRise);
      i===0 ? ctx.moveTo(x,y) : ctx.lineTo(x,y);
    });
    ctx.strokeStyle=GREEN; ctx.lineWidth=2; ctx.setLineDash([]); ctx.stroke();

    // Gradient fill under curve
    const grad = ctx.createLinearGradient(0,PT,0,PT+CH2);
    grad.addColorStop(0,GREEN+"40"); grad.addColorStop(1,GREEN+"00");
    ctx.fillStyle=grad;
    ctx.beginPath();
    s.r_history.forEach((d,i) => {
      const x = PL + ((d.t-t0)/(tEnd-t0||1)) * CW;
      const y = PT + CH2*(1-(d.a-R0)/maxRise);
      i===0 ? ctx.moveTo(x,y) : ctx.lineTo(x,y);
    });
    const lastD = s.r_history[s.r_history.length-1];
    ctx.lineTo(PL + CW, PT+CH2); ctx.lineTo(PL, PT+CH2); ctx.closePath(); ctx.fill();

    // Current point
    const lx = PL + CW;
    const ly = PT + CH2*(1-(lastD.a-R0)/maxRise);
    circle(ctx, lx, ly, 4, GREEN, null);

    // Stats overlay
    ctx.fillStyle=GOLD; ctx.font=`bold 10px ${MONO}`; ctx.textAlign="right";
    ctx.fillText(`▲ ${(s.total_rise_m/1000).toFixed(3)} km total rise`, PL+CW-4, PT+18);
    ctx.fillStyle=CYAN; ctx.font=`9px ${MONO}`;
    ctx.fillText(`${s.orbit_count} orbits  ·  Σ Δv = ${(s.delta_v_total).toFixed(4)} m/s`, PL+CW-4, PT+30);

    ctx.fillStyle=MUTED; ctx.font=`7px ${MONO}`; ctx.textAlign="center";
    ctx.fillText("physics time →", PL+CW/2, PT+CH2+PB-4);
  }

  // ── RAF loop ───────────────────────────────────────────────────────────────
  const loop = useCallback((ts) => {
    if (!playR.current) return;
    const dtWall = lastTsR.current ? Math.min((ts-lastTsR.current)/1000, 0.05) : 0.016;
    lastTsR.current = ts;
    const s = simRef.current;

    // Update emms_omega whenever freqRatio changes
    const T_cur = orbitalPeriod(s.orbit_a);
    s.emms_omega = (2*Math.PI/T_cur) * fRatioR.current;

    const dtPhys = dtWall * SIM_SPD;
    const SUBS   = 40;
    const dt     = dtPhys / SUBS;
    for (let i=0; i<SUBS; i++) step(s, dt);

    if (canvOrbit.current)  drawOrbit(canvOrbit.current, s);
    if (canvPend.current)   drawPendulum(canvPend.current, s);

    frameR.current++;
    if (frameR.current % 3 === 0) {
      if (canvChart.current) drawChart(canvChart.current, s);
      setDisp({
        alt:  (s.orbit_a-R_E)/1000,
        rise: s.total_rise_m/1000,
        dv:   s.delta_v_total,
        orbs: s.orbit_count,
        emmsPhase: ((s.emms_omega*s.t)%(2*Math.PI)).toFixed(2),
        satTheta:  (s.sat_theta*(180/Math.PI)).toFixed(1),
        emmsPosM:  s.emms_x.toFixed(4),
      });
    }
    rafRef.current = requestAnimationFrame(loop);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (playing) {
      lastTsR.current = null;
      rafRef.current = requestAnimationFrame(loop);
    } else {
      cancelAnimationFrame(rafRef.current);
    }
    return () => cancelAnimationFrame(rafRef.current);
  }, [playing, loop]);

  // Static render when not playing
  useEffect(() => {
    const s = simRef.current;
    if (canvOrbit.current)  drawOrbit(canvOrbit.current, s);
    if (canvPend.current)   drawPendulum(canvPend.current, s);
    if (canvChart.current)  drawChart(canvChart.current, s);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleReset = () => {
    setPlaying(false);
    cancelAnimationFrame(rafRef.current);
    simRef.current = freshSim(fRatioR.current);
    frameR.current = 0;
    lastTsR.current = null;
    setDisp(null);
    setTimeout(() => {
      const s = simRef.current;
      if (canvOrbit.current) drawOrbit(canvOrbit.current, s);
      if (canvPend.current)  drawPendulum(canvPend.current, s);
      if (canvChart.current) drawChart(canvChart.current, s);
    }, 20);
  };

  const isResonant = Math.abs(freqRatio*2 - Math.round(freqRatio*2)) < 0.08;

  return (
    <div style={{ background:BG, color:"#c0d8e8", fontFamily:MONO, padding:"16px 12px" }}>

      {/* Title */}
      <div style={{ textAlign:"center", marginBottom:14 }}>
        <div style={{ fontSize:15, fontWeight:700, color:GOLD, letterSpacing:3 }}>
          PENDULUM RESONANT ORBIT RISE
        </div>
        <div style={{ fontSize:9, color:MUTED, letterSpacing:2, marginTop:3 }}>
          EP-ADCS EMMS · PARAMETRIC ENERGY TRANSFER · NO PROPELLANT ORBIT RAISING
        </div>
      </div>

      {/* Concept callout */}
      <div style={{
        background:"#0a1a08", border:`1px solid ${GREEN}33`,
        borderLeft:`3px solid ${GREEN}`, borderRadius:"0 8px 8px 0",
        padding:"10px 16px", marginBottom:14, fontSize:11, lineHeight:1.9, color:"#a0c8b0",
      }}>
        Like pumping a playground swing — the child shifts weight at just the right moment each oscillation.
        The EMMS mass swings at{' '}
        <span style={{color:GOLD,fontWeight:700}}>ω_EMMS = n × ω_orbit</span>.
        Each half-stroke lands at the same orbital phase → Δv always tangential → orbit rises with{' '}
        <span style={{color:GREEN,fontWeight:700}}>zero propellant.</span>
        {' '}Off resonance: impulses cancel out → orbit stays flat.
      </div>

      {/* Controls */}
      <div style={{
        background:"#050f1a", border:"1px solid #0e2236",
        borderRadius:8, padding:"10px 16px", marginBottom:12,
        display:"flex", gap:12, flexWrap:"wrap", alignItems:"center",
      }}>
        <button
          onClick={() => setPlaying(p=>!p)}
          style={{
            fontFamily:MONO, fontSize:10, letterSpacing:1.5, padding:"7px 20px",
            borderRadius:5, cursor:"pointer",
            background: playing?"#2d0f15":"#0d2510",
            border:`1px solid ${playing?RED:GREEN}`,
            color: playing?RED:GREEN,
          }}>
          {playing?"■  PAUSE":"▶  PLAY"}
        </button>
        <button onClick={handleReset}
          style={{
            fontFamily:MONO,fontSize:10,letterSpacing:1.5,padding:"7px 18px",
            borderRadius:5,cursor:"pointer",background:"transparent",
            border:`1px solid ${MUTED}`,color:MUTED,
          }}>
          ⟳  RESET
        </button>

        {/* Frequency ratio slider — THE key control */}
        <div style={{ flex:1, minWidth:220 }}>
          <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
            <span style={{ fontSize:9, color:MUTED, letterSpacing:1.5, width:180, flexShrink:0 }}>
              EMMS FREQ / ORBIT FREQ
            </span>
            <input type="range" min={0.25} max={3} step={0.05} value={freqRatio}
              onChange={e=>{ setFreqRatio(parseFloat(e.target.value)); }}
              style={{ flex:1, accentColor:GOLD, cursor:"pointer" }} />
            <span style={{ fontSize:12, color:isResonant?GREEN:RED, fontWeight:700, width:60, textAlign:"right" }}>
              {freqRatio.toFixed(2)}×
            </span>
          </div>
          {/* Resonance markers */}
          <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
            {[0.5, 1.0, 1.5, 2.0, 3.0].map(r=>(
              <button key={r}
                onClick={()=>setFreqRatio(r)}
                style={{
                  fontFamily:MONO,fontSize:8,padding:"2px 8px",borderRadius:3,cursor:"pointer",
                  background: Math.abs(freqRatio-r)<0.03?GREEN+"22":"transparent",
                  border:`1px solid ${Math.abs(freqRatio-r)<0.03?GREEN:MUTED}`,
                  color:Math.abs(freqRatio-r)<0.03?GREEN:MUTED,
                }}>
                {r}:1
              </button>
            ))}
            <span style={{ fontSize:8, color:MUTED, alignSelf:"center" }}>← resonant ratios</span>
          </div>
        </div>

        <div style={{
          padding:"6px 12px", borderRadius:6,
          background: isResonant?"#031a0a":"#1a0308",
          border:`1px solid ${isResonant?GREEN:RED}`,
          fontSize:9, color:isResonant?GREEN:RED, fontWeight:700,
        }}>
          {isResonant?"✓ IN RESONANCE":"✗ OFF RESONANCE"}
        </div>
      </div>

      {/* Main animation panels */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:10 }}>
        {/* Orbit canvas */}
        <div>
          <div style={{ fontSize:8, color:MUTED, letterSpacing:2, marginBottom:4 }}>
            ORBITAL VIEW  —  altitude rises in resonance
          </div>
          <canvas ref={canvOrbit} width={700} height={380}
            style={{ width:"100%", height:"auto", display:"block",
              border:`1px solid ${GREEN}22`, borderRadius:8, background:BG }} />
        </div>
        {/* Pendulum / phase canvas */}
        <div>
          <div style={{ fontSize:8, color:MUTED, letterSpacing:2, marginBottom:4 }}>
            EMMS PENDULUM  +  PHASE PORTRAIT
          </div>
          <canvas ref={canvPend} width={700} height={380}
            style={{ width:"100%", height:"auto", display:"block",
              border:`1px solid ${GOLD}22`, borderRadius:8, background:BG }} />
        </div>
      </div>

      {/* Altitude chart */}
      <div style={{ marginBottom:12 }}>
        <div style={{ fontSize:8, color:MUTED, letterSpacing:2, marginBottom:4 }}>
          ALTITUDE GAIN CHART  —  flat when off resonance, rising when locked
        </div>
        <canvas ref={canvChart} width={1440} height={140}
          style={{ width:"100%", height:"auto", display:"block",
            border:`1px solid ${GREEN}22`, borderRadius:8, background:BG }} />
      </div>

      {/* Live metrics */}
      <div style={{
        background:"#050f1a", border:"1px solid #0e2236",
        borderRadius:8, padding:"12px 16px",
        display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:8,
      }}>
        {[
          { label:"ALTITUDE",     val: disp?(disp.alt.toFixed(2)+" km"):"500.00 km",  color:GREEN  },
          { label:"TOTAL RISE",   val: disp?("+"+disp.rise.toFixed(4)+" km"):"—",     color:GOLD   },
          { label:"Σ Δv",         val: disp?(disp.dv.toFixed(5)+" m/s"):"—",          color:CYAN   },
          { label:"ORBITS",       val: disp?disp.orbs:"0",                             color:CYAN   },
          { label:"SAT ANGLE",    val: disp?(disp.satTheta+"°"):"0°",                  color:MUTED  },
          { label:"EMMS POS",     val: disp?(parseFloat(disp.emmsPosM).toFixed(3)+" m"):"—", color:GOLD },
          { label:"RESONANCE",    val: isResonant?"LOCKED":"DRIFTING",                 color:isResonant?GREEN:RED },
        ].map(({ label, val, color }) => (
          <div key={label} style={{
            background:"#030d14", borderRadius:4, padding:"8px 10px",
            border:`1px solid #0e2236`,
          }}>
            <div style={{ fontSize:7, color:MUTED, letterSpacing:1.5, marginBottom:3 }}>{label}</div>
            <div style={{ fontSize:12, color, fontWeight:700 }}>{val}</div>
          </div>
        ))}
      </div>

      {/* Physics explainer */}
      <div style={{
        background:"#050f1a", border:"1px solid #0e2236",
        borderRadius:8, padding:"14px 18px", marginTop:12,
        display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:16,
      }}>
        {[
          {
            title:"RESONANCE CONDITION",
            col: GREEN,
            lines:[
              `ω_EMMS = n × ω_orbit`,
              `ω_orbit = √(µ / r³)`,
              `T_orbit = ${(orbitalPeriod(R0)/60).toFixed(1)} min at 500km`,
              `When n=1: 1 EMMS cycle per orbit`,
              `When n=2: 2 EMMS cycles per orbit`,
              `Each crossing lands at same orbital phase → Δv adds`,
            ]
          },
          {
            title:"ENERGY TRANSFER",
            col: GOLD,
            lines:[
              `Δv per stroke = (m_emms/M_total) × 2v_peak`,
              `v_peak = L_stroke × ω_EMMS`,
              `= ${(L_STROKE * 2*Math.PI/orbitalPeriod(R0)).toFixed(5)} m/s per stroke`,
              `Δa = 2a² × v_orb × Δv / µ`,
              `Off resonance: phases cancel → Δa≈0`,
              `In resonance: phases add → orbit rises`,
            ]
          },
          {
            title:"PENDULUM ANALOGY",
            col: PUR,
            lines:[
              `Child pumping a swing:`,
              `  → shifts weight at each apex`,
              `  → always in phase with swing`,
              `  → energy builds up resonantly`,
              `EMMS mass = child on swing`,
              `Orbital period = swing period`,
            ]
          },
        ].map(({ title, col, lines }) => (
          <div key={title}>
            <div style={{ fontSize:8, color:col, letterSpacing:2, fontWeight:700, marginBottom:8 }}>{title}</div>
            {lines.map((l,i)=>(
              <div key={i} style={{ fontSize:10, color: l.startsWith("  ")?MUTED:"#c0d0e0", lineHeight:1.8 }}>{l}</div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
